import { getToken } from 'auth0Provider'
import { fetchUtils, GetOneParams } from 'react-admin'
import { getGeo } from 'services/geo-management'
import getMockedResponse from 'services/mockResponses'
import { keys } from 'services/utils'
import { encodeQueryStringFromQueryObject } from 'services/utils/queryStringEncoding'
import kampaayDataProvider from '.'
import {
  APICfg,
  GetPathParams,
  HttpClientResponse,
  HttpOptions,
  RESTMethod,
  UpdateMethod,
} from './../api'
import RESOURCE_API_MAP, { APIEntity } from './../entities'
import { BrowserUploadFile } from './../entities/entities'

export const BASE_URL = process.env.REACT_APP_API_BASE_URL

export const httpClient = async (url: string, options: HttpOptions) => {
  const jwt = await getToken()
  return fetchUtils.fetchJson(url, {
    ...options,

    headers: new Headers({
      'x-kampaay-area': getGeo().toString(),
      ...(jwt?.authToken ? { Authorization: `Bearer ${jwt.authToken}` } : {}),
      ...(options.headers ? options.headers : {}),
    }),
  })
}

// FIXME: Investigate and rewrite this in a better way!!
export const throwErrorWithMessage = (err: any) => {
  if (err.body) {
    const { errors } = err.body
    if (errors) {
      err.message = keys(errors)
        .reduce((acc, key) => [...acc, ...errors[key]], [] as string[])
        .join('\n')
    } else if (err.body.title) {
      err.message = err.body.title
    } else {
      err.message = err.body
    }
  }
  throw err
}

export const getPath = (
  apiCfg: APICfg,
  params: Partial<GetPathParams> = {},
  isMany = false
) => {
  const { id, pagination, sort } = params

  // If we are calling the /many endpoint we can't pass any custom query
  // the only things we need are the ids
  let filter = { ...params.filter, ...(!isMany ? apiCfg.query : {}) }

  const { endpoint, isSingleEntity = false, getOneEndpoint } = apiCfg

  if (!isMany && !isSingleEntity && !!id)
    return (getOneEndpoint ?? `${endpoint}/:id`).replace(':id', id.toString())

  const path = isMany ? `${endpoint}/many` : endpoint
  return `${path}?${encodeQueryStringFromQueryObject({
    pagination,
    filter,
    sort,
  })}`
}

export const callApi = async (
  apiCfg: APICfg,
  path: string,
  resource: APIEntity, // NB Resource is only used for mocking purposes
  isList = false,
  method: RESTMethod = 'GET',
  data?: unknown
) => {
  const url = `${BASE_URL}/${path}`
  try {
    const res: HttpClientResponse = await httpClient(url, {
      method,
      body: JSON.stringify(data),
    })
    return formatResponse(apiCfg, res, method)
  } catch (error: any) {
    // TODO: remove this when we'll unmock EVERYTHING!
    if (error.status === 404) {
      const FAKE_HEADERS = new Headers()
      FAKE_HEADERS.append('x-kampaay-total-count', '50')
      return formatResponse(
        apiCfg,
        {
          status: 200,
          json: getMockedResponse(resource, isList ? 'list' : 'detail', url),
          headers: FAKE_HEADERS,
        },
        method
      )
    }
    throwErrorWithMessage(error)
  }
}

const getTotalFromHeaders = (h: Headers) =>
  !!h.get('x-kampaay-total-count')
    ? parseInt(h.get('x-kampaay-total-count')!)
    : 500

export const formatResponse = (
  { parse, isSingleEntity }: APICfg,
  { json, status, headers }: HttpClientResponse,
  method: RESTMethod
) => {
  const data =
    method === 'GET' || (isSingleEntity && method !== 'DELETE')
      ? Array.isArray(json)
        ? json.map(parse)
        : parse(json)
      : json
  return {
    status: status,
    data: data ?? {},
    total: getTotalFromHeaders(headers),
  }
}

export const callUpdate = async (
  apiCfg: APICfg,
  path: string,
  method: UpdateMethod = 'PUT',
  data: any
) => {
  const url = `${BASE_URL}/${path}`
  return httpClient(url, { method, body: JSON.stringify(data) })
    .then((res) => formatResponse(apiCfg, res, method))
    .catch((err) => {
      throwErrorWithMessage(err)
    })
}

export const callPost = async (
  apiCfg: APICfg,
  path: string,
  method: RESTMethod,
  data: any
) => {
  const url = `${BASE_URL}/${path}`
  return httpClient(url, { method, body: JSON.stringify(data) })
    .then((res) => formatResponse(apiCfg, res, method))
    .catch((err) => {
      throwErrorWithMessage(err)
    })
}

export const getApiCfg = (resource: APIEntity) => RESOURCE_API_MAP[resource]

export const upload = async (
  resource: 'image' | 'doc',
  rawFile: any,
  key: string
) => {
  const formData = new FormData()
  formData.append(key, rawFile)
  const url = `${BASE_URL}/uploads/${resource}`
  const { json } = await httpClient(url, {
    method: 'POST',
    body: formData,
  })
  return json
}

export const uploadDoc = async (file: BrowserUploadFile) => {
  const res = await upload('doc', file, 'Document')
  return res.id as string
}
export const uploadImg = async (file: BrowserUploadFile) => {
  const res = await upload('image', file, 'image')
  return res.id as string
}

export const multiPlexGetOneHandler = async (
  params: GetOneParams,
  ...entities: APIEntity[]
) => {
  const apiCfgArray = entities.map(getApiCfg)
  const res = await Promise.all(
    apiCfgArray.map((config) =>
      callApi(config, getPath(config, params), entities[0] as APIEntity)
    )
  )
  return res
}

type MultiPlexUpdateTarget = {
  resource: APIEntity
  params: { data: any; id: number }
}

export const multiPlexUpdateHandler = async (
  ...targets: MultiPlexUpdateTarget[]
) => {
  // this executes all the promises sequentially and in the order that we pass the targets
  // we should always pass the main resource first
  let cachedError
  for (const { resource, params } of targets) {
    try {
      const apiCfg = getApiCfg(resource)
      const mappedData = await apiCfg.write(params.data, kampaayDataProvider)
      await callUpdate(
        apiCfg,
        getPath(apiCfg, params),
        apiCfg.UPDATE_METHOD,
        mappedData
      )
    } catch (error) {
      cachedError = error
    }
  }
  if (!!cachedError) throw cachedError

  return { data: { id: 'ok' } }
}
